import React, { FC, useState } from 'react';
import { Typography, Modal, Progress, message } from 'antd';
import { useForm } from '../../lib/hooks/useForm/useForm';
import { FormState } from '../../lib/hooks/useForm/types';
import { AuthorReleaseForm } from './components/AuthorReleaseForm';
import { AuthorReleaseFormValues, ContentType, ReleaseProgress } from './components/types';
import { useAuthorReleaseApi } from './hooks/useAuthorReleaseApi';

const { Title, Text } = Typography;

const validateAuthorReleaseForm = (state: FormState<AuthorReleaseFormValues>) => {
	const values = state.value;

	// 1. Validações principais do Select
	const contentTypeError = !values?.content_type ? 'Selecione o tipo de conteúdo' : '';
	const publicationError = !values?.publication_id ? 'Selecione a publicação' : '';
	const editionError = !values?.edition_id ? 'Selecione a edição' : '';

	// 2. Validação dos Autores (CPF e Email obrigatórios)
	let authorsError = '';
	if (values?.authors && values.authors.length > 0) {
		const hasInvalidAuthor = values.authors.some((author) => {
			const cleanCpf = author.cpf ? author.cpf.replace(/\D/g, '') : '';
			const cleanEmail = author.email ? author.email.trim() : '';

			// CPF deve ter exatamente 11 dígitos e Email não pode estar vazio
			return cleanCpf.length !== 11 || cleanEmail.length === 0;
		});

		if (hasInvalidAuthor) {
			authorsError = 'Todos os autores devem ter CPF válido e E-mail preenchidos.';
		}
	}

	// 3. Validação das Pessoas Extras (Nome e Email obrigatórios, se houver)
	let extraPeopleError = '';
	if (values?.extra_people && values.extra_people.length > 0) {
		const hasInvalidExtra = values.extra_people.some((person) => {
			const cleanName = person.name ? person.name.trim() : '';
			const cleanEmail = person.email ? person.email.trim() : '';

			return cleanName.length === 0 || cleanEmail.length === 0;
		});

		if (hasInvalidExtra) {
			extraPeopleError = 'Todas as pessoas extras devem ter Nome e E-mail preenchidos.';
		}
	}

	return {
		content_type: contentTypeError,
		publication_id: publicationError,
		edition_id: editionError,
		authors: authorsError,
		extra_people: extraPeopleError,
	};
};

export const AuthorReleasePage: FC = () => {
	const { get_publications, get_editions, get_authors, send_release, loading } = useAuthorReleaseApi();

	// Estado do Modal de Progresso e Trava da Tela
	const [progressModalVisible, setProgressModalVisible] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false); // <--- Estado para bloquear o formulário
	const [progress, setProgress] = useState<ReleaseProgress>({
		step: 'idle',
		percent: 0,
		message: 'Iniciando liberação...',
	});

	const { form_state, change_field_value } = useForm<AuthorReleaseFormValues>(
		{
			content_type: undefined,
			publication_id: undefined,
			edition_id: undefined,
			authors: [],
			extra_people: [],
		},
		validateAuthorReleaseForm
	);

	const handleContentTypeChange = (type: ContentType, search: string = '') => {
		if (type === 'livro') {
			get_publications.get('livro', search);
		} else if (type === 'revista') {
			get_publications.get('revista');
		}
	};

	const handlePublicationChange = (publication_id: string) => {
		get_editions.get(publication_id);
	};

	const handleEditionChange = async (edition_id: string) => {
		const currentType = form_state.value.content_type || 'livro';
		const authors = await get_authors.get(currentType, edition_id);

		if (authors && authors.length > 0) {
			change_field_value('authors', authors);
		} else {
			change_field_value('authors', []);
		}
	};

	const handleSubmit = () => {
		const values = form_state.value;

		// Checagem de segurança nos Autores
		if (values.authors && values.authors.length > 0) {
			const missingAuthorData = values.authors.find((author) => {
				const cleanCpf = author.cpf ? author.cpf.replace(/\D/g, '') : '';
				const cleanEmail = author.email ? author.email.trim() : '';
				return cleanCpf.length !== 11 || !cleanEmail;
			});

			if (missingAuthorData) {
				message.error('Por favor, preencha corretamente o CPF e o E-mail de todos os autores da edição.');
				return;
			}
		}

		// Checagem de segurança nas Pessoas Extras
		if (values.extra_people && values.extra_people.length > 0) {
			const missingExtraData = values.extra_people.find((p) => !p.name?.trim() || !p.email?.trim());

			if (missingExtraData) {
				message.error('Por favor, preencha o Nome e o E-mail de todas as pessoas extras adicionadas.');
				return;
			}
		}

		// Se tudo estiver preenchido, abre o modal e dispara a liberação
		setProgressModalVisible(true);
		setProgress({
			step: 'profiles',
			percent: 10,
			message: 'Iniciando o processamento...',
		});

		send_release.send(form_state.value, (currentProgress) => {
			setProgress(currentProgress);

			if (currentProgress.step === 'done') {
				setIsSubmitted(true);
				message.success('Liberação de autores e contratos efetuados com sucesso!');

				setTimeout(() => {
					setProgressModalVisible(false);
				}, 1500);
			}
		});
	};

	// const handleSubmit = () => {
	// 	setProgressModalVisible(true);
	// 	setProgress({
	// 		step: 'profiles',
	// 		percent: 10,
	// 		message: 'Iniciando o processamento...',
	// 	});

	// 	send_release.send(form_state.value, (currentProgress) => {
	// 		setProgress(currentProgress);

	// 		if (currentProgress.step === 'done') {
	// 			setIsSubmitted(true); // <--- Bloqueia o formulário no término
	// 			message.success('Liberação de autores e contratos efetuados com sucesso!');

	// 			setTimeout(() => {
	// 				setProgressModalVisible(false);
	// 			}, 1500);
	// 		}
	// 	});
	// };

	return (
		<div style={{ padding: 24 }}>
			<Title level={3}>Liberação de Autores</Title>

			<AuthorReleaseForm
				form_state={form_state}
				change_field_value={change_field_value}
				loading={loading}
				disabled={isSubmitted} // <--- Passa a prop disabled para travar os campos
				publication_loading={get_publications.loading}
				edition_loading={get_editions.loading}
				on_submit={handleSubmit}
				publication_options={get_publications.result}
				edition_options={get_editions.result}
				on_change_content_type={handleContentTypeChange}
				on_change_publication={handlePublicationChange}
				on_change_edition={handleEditionChange}
			/>

			{/* Modal com indicador de progresso dinâmico */}
			<Modal
				title='Processando Liberação de Autores'
				open={progressModalVisible}
				footer={null}
				closable={false}
				maskClosable={false}
				centered
			>
				<div style={{ textAlign: 'center', padding: '24px 0' }}>
					<Progress
						type='circle'
						percent={progress.percent}
						status={progress.step === 'done' ? 'success' : 'active'}
						style={{ marginBottom: 20 }}
					/>
					<div>
						<Text strong style={{ fontSize: 16 }}>
							{progress.message}
						</Text>
					</div>
				</div>
			</Modal>
		</div>
	);
};
// import React, { FC, useState } from 'react';
// import { Typography, Modal, Progress } from 'antd';
// import { useForm } from '../../lib/hooks/useForm/useForm';
// import { FormState } from '../../lib/hooks/useForm/types';
// import { AuthorReleaseForm } from './components/AuthorReleaseForm';
// import { AuthorReleaseFormValues, ContentType, ReleaseProgress } from './components/types';
// import { useAuthorReleaseApi } from './hooks/useAuthorReleaseApi';

// const { Title, Text } = Typography;

// const validateAuthorReleaseForm = (state: FormState<AuthorReleaseFormValues>) => {
// 	const values = state.value;

// 	return {
// 		content_type: !values?.content_type ? 'Selecione o tipo de conteúdo' : '',
// 		publication_id: !values?.publication_id ? 'Selecione a publicação' : '',
// 		edition_id: !values?.edition_id ? 'Selecione a edição' : '',
// 		authors: '',
// 		extra_people: '',
// 	};
// };

// export const AuthorReleasePage: FC = () => {
// 	const { get_publications, get_editions, get_authors, send_release, loading } = useAuthorReleaseApi();

// 	// Estado do Modal de Progresso
// 	const [progressModalVisible, setProgressModalVisible] = useState(false);
// 	const [progress, setProgress] = useState<ReleaseProgress>({
// 		step: 'idle',
// 		percent: 0,
// 		message: 'Iniciando liberação...',
// 	});

// 	const { form_state, change_field_value } = useForm<AuthorReleaseFormValues>(
// 		{
// 			content_type: undefined,
// 			publication_id: undefined,
// 			edition_id: undefined,
// 			authors: [],
// 			extra_people: [],
// 		},
// 		validateAuthorReleaseForm
// 	);

// 	const handleContentTypeChange = (type: ContentType, search: string = '') => {
// 		if (type === 'livro') {
// 			get_publications.get('livro', search);
// 		} else if (type === 'revista') {
// 			get_publications.get('revista');
// 		}
// 	};

// 	const handlePublicationChange = (publication_id: string) => {
// 		get_editions.get(publication_id);
// 	};

// 	const handleEditionChange = async (edition_id: string) => {
// 		const currentType = form_state.value.content_type || 'livro';
// 		const authors = await get_authors.get(currentType, edition_id);

// 		if (authors && authors.length > 0) {
// 			change_field_value('authors', authors);
// 		} else {
// 			change_field_value('authors', []);
// 		}
// 	};

// 	const handleSubmit = () => {
// 		// Abre o modal e zera a barra de progresso
// 		setProgressModalVisible(true);
// 		setProgress({
// 			step: 'profiles',
// 			percent: 10,
// 			message: 'Iniciando o processamento...',
// 		});

// 		// Dispara o envio passando o callback de progresso
// 		send_release.send(form_state.value, (currentProgress) => {
// 			setProgress(currentProgress);

// 			// Ao concluir (100%), aguarda 1.5s antes de fechar o modal
// 			if (currentProgress.step === 'done') {
// 				setTimeout(() => {
// 					setProgressModalVisible(false);
// 				}, 1500);
// 			}
// 		});
// 	};

// 	return (
// 		<div style={{ padding: 24 }}>
// 			<Title level={3}>Liberação de Autores</Title>

// 			<AuthorReleaseForm
// 				form_state={form_state}
// 				change_field_value={change_field_value}
// 				loading={loading}
// 				publication_loading={get_publications.loading}
// 				edition_loading={get_editions.loading}
// 				on_submit={handleSubmit}
// 				publication_options={get_publications.result}
// 				edition_options={get_editions.result}
// 				on_change_content_type={handleContentTypeChange}
// 				on_change_publication={handlePublicationChange}
// 				on_change_edition={handleEditionChange}
// 			/>

// 			{/* Modal com indicador de progresso dinâmico */}
// 			<Modal
// 				title='Processando Liberação de Autores'
// 				open={progressModalVisible}
// 				footer={null}
// 				closable={false}
// 				maskClosable={false}
// 				centered
// 			>
// 				<div style={{ textAlign: 'center', padding: '24px 0' }}>
// 					<Progress
// 						type='circle'
// 						percent={progress.percent}
// 						status={progress.step === 'done' ? 'success' : 'active'}
// 						style={{ marginBottom: 20 }}
// 					/>
// 					<div>
// 						<Text strong style={{ fontSize: 16 }}>
// 							{progress.message}
// 						</Text>
// 					</div>
// 				</div>
// 			</Modal>
// 		</div>
// 	);
// };
// // import React, { FC } from 'react';
// // import { Typography } from 'antd';
// // import { useForm } from '../../lib/hooks/useForm/useForm';
// // import { FormState } from '../../lib/hooks/useForm/types';
// // import { AuthorReleaseForm } from './components/AuthorReleaseForm';
// // import { AuthorReleaseFormValues, ContentType } from './components/types';
// // import { useAuthorReleaseApi } from './hooks/useAuthorReleaseApi';

// // const { Title } = Typography;

// // // Ajustado para retornar um objeto fortemente tipado com as chaves de AuthorReleaseFormValues
// // const validateAuthorReleaseForm = (state: FormState<AuthorReleaseFormValues>) => {
// // 	const values = state.value;

// // 	return {
// // 		content_type: !values?.content_type ? 'Selecione o tipo de conteúdo' : '',
// // 		publication_id: !values?.publication_id ? 'Selecione a publicação' : '',
// // 		edition_id: !values?.edition_id ? 'Selecione a edição' : '',
// // 		authors: '',
// // 		extra_people: '',
// // 	};
// // };

// // export const AuthorReleasePage: FC = () => {
// // 	const { get_publications, get_editions, get_authors, send_release, loading } = useAuthorReleaseApi();

// // 	const { form_state, change_field_value } = useForm<AuthorReleaseFormValues>(
// // 		{
// // 			content_type: undefined, // Alterado: Inicializa sem nenhum tipo selecionado
// // 			publication_id: undefined,
// // 			edition_id: undefined,
// // 			authors: [],
// // 			extra_people: [],
// // 		},
// // 		validateAuthorReleaseForm
// // 	);

// // 	const handleContentTypeChange = (type: ContentType, search: string = '') => {
// // 		if (type === 'livro') {
// // 			// Busca livros enviando a string de busca (ou vazia para inicial)
// // 			get_publications.get('livro', search);
// // 		} else if (type === 'revista') {
// // 			// Busca periódicos
// // 			get_publications.get('revista');
// // 		}
// // 	};
// // 	const handlePublicationChange = (publication_id: string) => {
// // 		get_editions.get(publication_id);
// // 	};

// // 	const handleEditionChange = async (edition_id: string) => {
// // 		const currentType = form_state.value.content_type || 'livro';
// // 		const authors = await get_authors.get(currentType, edition_id);

// // 		if (authors && authors.length > 0) {
// // 			change_field_value('authors', authors);
// // 		} else {
// // 			change_field_value('authors', []);
// // 		}
// // 	};
// // 	const handleSubmit = () => {
// // 		send_release.send(form_state.value);
// // 	};

// // 	return (
// // 		<div style={{ padding: 24 }}>
// // 			<Title level={3}>Liberação de Autores</Title>

// // 			<AuthorReleaseForm
// // 				form_state={form_state}
// // 				change_field_value={change_field_value}
// // 				loading={loading}
// // 				on_submit={handleSubmit}
// // 				publication_options={get_publications.result}
// // 				edition_options={get_editions.result}
// // 				on_change_content_type={handleContentTypeChange}
// // 				on_change_publication={handlePublicationChange}
// // 				on_change_edition={handleEditionChange}
// // 			/>
// // 		</div>
// // 	);
// // };
